import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  Stack,
  LinearProgress,
  Alert,
  AlertTitle,
} from '@mui/material';
import { useMemo } from 'react';
import {
  processMaintenanceInfo,
  useMaintenanceInfo,
} from '@features/admin/adminAPI';
import BuildIcon from '@mui/icons-material/Build';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DateTime } from 'luxon';

function MaintenancePage() {
  const { data, fulfilledTimeStamp } = useMaintenanceInfo();

  const info = useMemo(() => {
    if (data) return processMaintenanceInfo(data);
  }, [data]);

  const startDateFormatted = useMemo(() => {
    if (info) return info.formattedStartDate;
    return 'N/A';
  }, [info]);
  const endDateFormatted = useMemo(() => {
    if (info) return info.formattedEndDate;
    return 'N/A';
  }, [info]);
  const reason = useMemo(() => {
    if (data?.reason) return data.reason;
    return 'We are performing scheduled maintenance to improve our services and enhance your experience.';
  }, [data]);

  const lastUpdatedTimestamp = useMemo(() => {
    if (fulfilledTimeStamp)
      return DateTime.fromMillis(fulfilledTimeStamp).toLocaleString(
        DateTime.DATETIME_FULL,
      );
    return 'N/A';
  }, [fulfilledTimeStamp]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 4 },
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, #0f1729 0%, #1a2744 100%)'
            : 'linear-gradient(180deg, #f5f7fa 0%, #e8ecf1 100%)',
        zIndex: 999,
        position: 'relative',
      }}
    >
      <Box sx={{ maxWidth: 700, width: '100%' }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'rgba(77, 182, 172, 0.15)',
              mb: 3,
            }}
          >
            <BuildIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          </Box>

          <Typography
            variant="h2"
            component="h1"
            sx={{
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              color: 'text.primary',
            }}
          >
            Under Maintenance
          </Typography>

          <Chip
            icon={<ScheduleIcon sx={{ fontSize: 18 }} />}
            label="Scheduled Downtime"
            color="primary"
            variant="outlined"
            sx={{ mb: 3 }}
          />

          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              maxWidth: 500,
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            {reason}
          </Typography>
        </Box>

        {/* Progress Indicator */}
        <Box sx={{ mb: 4 }}>
          <LinearProgress
            variant="indeterminate"
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: 'rgba(77, 182, 172, 0.1)',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'primary.main',
              },
            }}
          />
        </Box>

        {/* Schedule Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography
              variant="h6"
              sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <CalendarTodayIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              Maintenance Schedule
            </Typography>

            <Stack spacing={3}>
              {/* Start Time */}
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: 'text.secondary',
                    letterSpacing: 1.5,
                    fontSize: '0.7rem',
                  }}
                >
                  Scheduled Start
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'baseline' },
                    gap: { xs: 0.5, sm: 2 },
                    mt: 0.5,
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'text.primary' }}>
                    {startDateFormatted}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

              {/* End Time */}
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: 'text.secondary',
                    letterSpacing: 1.5,
                    fontSize: '0.7rem',
                  }}
                >
                  Expected End
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'baseline' },
                    gap: { xs: 0.5, sm: 2 },
                    mt: 0.5,
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'text.primary' }}>
                    {endDateFormatted}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Info Alert */}
        <Alert
          severity="info"
          icon={<InfoOutlinedIcon />}
          sx={{
            bgcolor: 'rgba(77, 182, 172, 0.08)',
            border: '1px solid rgba(77, 182, 172, 0.2)',
            '& .MuiAlert-icon': {
              color: 'primary.main',
            },
          }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>What to expect</AlertTitle>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            During this maintenance window, all services will be temporarily
            unavailable. We apologize for any inconvenience and appreciate your
            patience.
          </Typography>
        </Alert>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Last updated: {lastUpdatedTimestamp}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default MaintenancePage;
